// NOTE: In development, React StrictMode may cause the submit button to appear non-functional
// due to double-invocation of blur event handlers in useKeyboardDetection. This is a development-only
// quirk and does not affect production builds where StrictMode is disabled.
import c from 'classnames';
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
  Button,
  Icon,
  LoadingLines,
  SpinnerCircular,
  Typography,
} from '@repo/ui';

import { useAnalytics } from '../../contexts/analytics/AnalyticsProvider';
import { useAutoResizeTextarea } from '../../hooks/useAutoResizeTextarea';
import { useCurrentKitAndTinyAccount } from '../../hooks/useCurrentKitAndTinyAccount';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { useTinyAIChatTRPC } from '../../hooks/useTinyAIChatTRPC';
import { useTinyAIDisplayLocation } from '../../hooks/useTinyAIDisplayLocation';
import { useTinyAIPageContext } from '../../hooks/useTinyAIPageContext';
import { useChatbotPrompts } from '../../services/chatbotPrompts/useChatbotPrompts';
import { getTinyAccountById } from '../../store/account';
import { getHomePageTinyAccountId } from '../../store/home';
import { TINYAI_CONSTANTS } from './constants';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { useKeyboardDetection } from './hooks/useKeyboardDetection';
import { AIMessage, LoadingLabel, UserMessage } from './Messages';
import { TinyAICloseButton } from './TinyAICloseButton';
import { useTinyAIContext } from './TinyAIContext';

import styles from './TinyAIChatWindow.module.scss';

interface TinyAIChatWindowProps {
  onKeyboardVisibilityChange?: (isVisible: boolean) => void;
  onClose?: () => void;
  scrollContainerRef?: RefObject<HTMLElement>;
}

export const TinyAIChatWindow = ({
  onKeyboardVisibilityChange,
  onClose,
  scrollContainerRef,
}: TinyAIChatWindowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messagesWithFeedback, setMessagesWithFeedback] = useState<Set<string>>(
    new Set(),
  );

  const { currentTinyAccount } = useCurrentKitAndTinyAccount();
  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const homePageTinyAccount = useSelector(
    getTinyAccountById(homePageTinyAccountId),
  );
  const tinyAccount = currentTinyAccount ?? homePageTinyAccount;

  const pageContext = useTinyAIPageContext();
  const { trackEvent } = useAnalytics();

  const { consumePendingPrompt, pendingPromptTrigger } = useTinyAIContext();
  const displayLocation = useTinyAIDisplayLocation();
  const { data: prompts } = useChatbotPrompts(displayLocation);

  const {
    messages,
    isLoading,
    isLoadingHistory,
    isLoadingMoreHistory,
    hasMoreHistory,
    sendMessage,
    cancelLoading,
    loadMoreHistory,
    retryLastMessage,
    streamingMessageId,
  } = useTinyAIChatTRPC({
    pageContext,
    tinyAccountId: tinyAccount?.id,
  });

  const handleFeedbackSubmitted = useCallback((messageId: string) => {
    setMessagesWithFeedback(prev => new Set(prev).add(messageId));
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const hasTrackedPromptShown = useRef(false);
  const prevScrollHeightRef = useRef<number>(0);
  const didPrependRef = useRef<boolean>(false);
  const userHasScrolledUpRef = useRef<boolean>(false);
  const justLoadedMoreHistoryRef = useRef<boolean>(false);
  const hasScrolledToBottomInitiallyRef = useRef<boolean>(false);

  const { isMobile } = useMobileDetection();
  const inputRef = useAutoResizeTextarea({ value: inputValue });

  useBodyScrollLock({
    enabled: isMobile,
    containerRef: scrollContainerRef,
  });

  const { isKeyboardVisible, viewportHeight, offsetTop } = useKeyboardDetection({
    enabled: isMobile,
    inputRef,
    onVisibilityChange: onKeyboardVisibilityChange,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const pending = consumePendingPrompt();
    if (pending) {
      trackEvent('TinyAI prompt hook clicked', {
        prompt_text: pending.text,
        display_location: pending.displayLocation,
        prompt_id: pending.promptId,
        page_context: pageContext,
      });
      trackEvent('TinyAI message sent', {
        page_context: pageContext,
        source: 'chat',
        prompt_text: pending.text,
        display_location: pending.displayLocation,
      });
      sendMessage(pending.text, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPromptTrigger]);

  useEffect(() => {
    if (
      prompts &&
      prompts.length > 0 &&
      messages.length === 0 &&
      !hasTrackedPromptShown.current
    ) {
      hasTrackedPromptShown.current = true;
      trackEvent('TinyAI prompt hook shown', {
        display_location: displayLocation!,
        prompt_count: prompts.length,
        page_context: pageContext,
      });
    }
  }, [prompts, messages.length, displayLocation, trackEvent, pageContext]);

  useEffect(() => {
    if (
      messages.length > 0 &&
      !userHasScrolledUpRef.current &&
      !justLoadedMoreHistoryRef.current
    ) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    justLoadedMoreHistoryRef.current = false;
  }, [messages]);

  // Scroll to bottom when conversation first loads. Fires once per conversation —
  // the flag resets whenever isLoadingHistory flips back to true (account switch)
  // so prepending older history via loadMoreHistory won't retrigger it.
  useEffect(() => {
    if (isLoadingHistory) {
      hasScrolledToBottomInitiallyRef.current = false;
      return;
    }
    if (messages.length > 0 && !hasScrolledToBottomInitiallyRef.current) {
      hasScrolledToBottomInitiallyRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [isLoadingHistory, messages.length]);

  useEffect(() => {
    if (
      messages.length > 0 &&
      messages[messages.length - 1].senderType === 'assistant'
    ) {
      trackEvent('TinyAI responded', {
        page_context: pageContext,
      });
    }
  }, [messages, trackEvent, pageContext]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    userHasScrolledUpRef.current = !isAtBottom;

    if (el.scrollTop <= 100 && hasMoreHistory && !isLoadingMoreHistory) {
      prevScrollHeightRef.current = el.scrollHeight;
      didPrependRef.current = true;
      justLoadedMoreHistoryRef.current = true;
      loadMoreHistory();
    }
  }, [hasMoreHistory, isLoadingMoreHistory, loadMoreHistory]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Preserve scroll position when older messages are prepended
  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !didPrependRef.current) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    didPrependRef.current = false;
  }, [messages.length]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const messageContent = inputValue.trim();
    setInputValue('');

    // Reset scroll flag so we auto-scroll to show the new message
    userHasScrolledUpRef.current = false;

    trackEvent('TinyAI message sent', {
      page_context: pageContext,
      source: 'chat',
    });

    await sendMessage(messageContent, false);
  };

  const handlePromptClick = async (promptText: string) => {
    trackEvent('TinyAI in-widget prompt clicked', {
      prompt_text: promptText,
      display_location: displayLocation!,
      page_context: pageContext,
    });
    trackEvent('TinyAI message sent', {
      page_context: pageContext,
      source: 'chat',
      prompt_text: promptText,
      display_location: displayLocation || undefined,
    });
    await sendMessage(promptText, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const swipeDistance = touchEndY - touchStartY.current;

    if (swipeDistance > TINYAI_CONSTANTS.SWIPE_THRESHOLD) {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  return (
    <>
      <div
        className={c(styles.chatWindow, {
          [styles.expanded]: isExpanded,
          [styles.keyboardOpen]: isMobile && isKeyboardVisible,
        })}
        style={
          isMobile && isKeyboardVisible && viewportHeight
            ? {
                height: `${viewportHeight - 32}px`,
                // Safety net: if iOS still shifts the visual viewport (offsetTop > 0),
                // pin the window to the shifted origin, preserving the CSS top margin.
                ...(offsetTop > 0
                  ? { top: `calc(${offsetTop}px + max(1rem, env(safe-area-inset-top)))` }
                  : {}),
              }
            : undefined
        }
      >
        <div
          className={styles.swipeTouchArea}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className={styles.swipeIndicator} />
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <div className={styles.header}>
              <div className={styles.titleSection}>
                <Icon
                  name='aiGradient'
                  className={styles.icon}
                  preserveColors
                />
                <div className={styles.titleWrapper}>
                  <Typography
                    variant='heading-xl'
                    className={styles.gradientText}
                  >
                    TinyAI
                  </Typography>
                  <Typography variant='label' className={styles.betaTag}>
                    Beta
                  </Typography>
                </div>
              </div>
              <button
                className={c(styles.expandButton, 'hide-on-mobile')}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
                aria-label={isExpanded ? 'Minimize' : 'Expand'}
              >
                <Icon name={isExpanded ? 'collapse' : 'expand'} size='s' />
              </button>
            </div>

            {messages.length === 0 && !isLoadingHistory && (
              <div className={styles.greeting}>
                <Typography variant='heading-l' color='purple'>
                  Hello there!
                </Typography>
                <Typography variant='body-m'>
                  How can I help with {tinyAccount?.first_name}'s results?
                </Typography>
                {prompts && prompts.length > 0 && (
                  <div className={styles.promptChips}>
                    {prompts.map(prompt => (
                      <button
                        key={prompt.id}
                        className={styles.promptChip}
                        onClick={() => handlePromptClick(prompt.prompt_text)}
                      >
                        <Icon
                          name='aiGradient'
                          size='s'
                          preserveColors
                          className={styles.promptChipIcon}
                        />
                        <Typography variant='body-s'>
                          {prompt.prompt_text}
                        </Typography>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isLoadingHistory && (
              <div className={styles.historyLoading}>
                <LoadingLines lines={10} />
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <div
              className={styles.messagesContainer}
              ref={messagesContainerRef}
            >
              {isLoadingMoreHistory && (
                <div className={styles.loadingMore}>
                  <SpinnerCircular
                    size={24}
                    color='var(--purple-300)'
                    secondaryColor='transparent'
                  />
                </div>
              )}
              {messages.map(message => (
                <div key={message.id} className={styles.messageWrapper}>
                  {message.senderType === 'user' ? (
                    <UserMessage message={message.messageText} />
                  ) : (
                    <AIMessage
                      message={message.messageText}
                      messageId={message.id}
                      isStreaming={message.id === streamingMessageId}
                      hasFeedback={messagesWithFeedback.has(message.id)}
                      onFeedbackSubmitted={handleFeedbackSubmitted}
                      metadataJson={message.metadataJson}
                      onRetry={retryLastMessage}
                    />
                  )}
                </div>
              ))}
              {isLoading && (
                <div className={styles.messageWrapper}>
                  <LoadingLabel />
                  <LoadingLines lines={3} />
                  <Button
                    variant='text'
                    size='small'
                    color='purple'
                    iconNameLeft='closeSm'
                    label='Cancel'
                    onClick={cancelLoading}
                    className={c(styles.cancelButton, 'mt-2')}
                  />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          <div className={styles.inputContainer}>
            <textarea
              ref={inputRef}
              className={styles.input}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Ask anything...'
              rows={1}
            />
            <button
              className={c(styles.sendButton, {
                [styles.active]: inputValue.trim(),
              })}
              onClick={handleSend}
              disabled={!inputValue.trim()}
              aria-label='Send message'
            >
              <Icon name='upSmall' size='s' />
            </button>
          </div>
        </div>
      </div>
      {isMobile && onClose && (
        <TinyAICloseButton
          onClick={onClose}
          isKeyboardVisible={isKeyboardVisible}
        />
      )}
    </>
  );
};
